import { useProfileContext } from "@hooks/context/useProfileContext";
import { Box, Link, useTheme } from "@mui/material";
import Logo from "@ui/atoms/Logo/Logo";
import {
  CATCHMENT_MARK_RATIO,
  CATCHMENT_WORDMARK_RATIO
} from "@ui/atoms/Logo/logoMarks";
import { SidebarLayoutType } from "@layout/MainSidebar/const";
import MainSidebarProfileSection from "@layout/MainSidebar/MainSidebarProfileSection/MainSidebarProfileSection";
import { useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import * as RouteHelper from "@utils/paths";
import { Link as RouterLink } from "react-router-dom";
import {
  ProjectDetailLineItem,
  ProjectLineItem
} from "catchment-common/interfaces/project";
import { NavItemListType } from "types";

import {
  SidebarContainer,
  SidebarHeader,
  SidebarLogo,
  SidebarLogoLayer,
  SidebarNav,
  SidebarDimRegion,
  SidebarRule,
  SidebarToggleButton
} from "./MainSidebar.components";
import MainSidebarMenu from "./MainSidebarMenu/MainSidebarMenu";
import { ScopeSwitcher } from "./ScopeSwitcher/ScopeSwitcher";

// The Catchment marks are 2631x330 and 443x330; both drawn 22px tall, so the
// wordmark (175px) and the mark (30px) share a baseline as the nav expands and
// collapses.
//
// EVEN, and that is the whole point. The brand row is 56px and centres what it
// holds, so an odd height leaves the mark on a HALF pixel — at 21 it sat at
// y 17.5, which puts every horizontal stroke in the wordmark across two device
// pixels and antialiases it into grey. That is the "blurry SVG": not the
// vector, its placement. 22 also divides 330 exactly 15 times.
const LOGO_HEIGHT = 22;
// Derived, never rounded — see `CATCHMENT_WORDMARK_RATIO`. A box that is not
// the mark's own ratio letterboxes it and lands it on a half pixel.
const WORDMARK_WIDTH = LOGO_HEIGHT * CATCHMENT_WORDMARK_RATIO;
const MARK_WIDTH = LOGO_HEIGHT * CATCHMENT_MARK_RATIO;

export interface Props {
  isDrawOpen: boolean;
  setIsDrawOpen: (value: boolean) => void;
  selectedItem: string;
  setSelectedItem: (value: string) => void;
  loading: boolean;
  menuItems: NavItemListType;
  /** Which scope the nav is showing — drives what the scope switcher marks
   * as selected, and which menu the layout handed in. */
  layoutType: SidebarLayoutType;
  /** Every project the switcher can offer. */
  projects: ProjectDetailLineItem[];
  currentProject?: ProjectLineItem;
  currentContractorPackageId?: string;
  switchProject: (projectId: string, contractorPackageId: string) => void;
}

export const MainSidebar = ({
  currentContractorPackageId,
  currentProject,
  isDrawOpen,
  layoutType,
  loading,
  menuItems,
  projects,
  selectedItem,
  setIsDrawOpen,
  setSelectedItem,
  switchProject
}: Props) => {
  const theme = useTheme();
  const { isAuthProfileLoading } = useProfileContext();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isScopeMenuOpen, setIsScopeMenuOpen] = useState(false);

  const isLoading = loading || isAuthProfileLoading;

  const toggleSidebar = () => setIsDrawOpen(!isDrawOpen);

  return (
    <SidebarContainer
      open={isDrawOpen}
      variant="permanent"
      data-test-id="project-sidebar-menu"
    >
      <SidebarNav open={isDrawOpen}>
        <SidebarDimRegion open={isDrawOpen} dimmed={isProfileMenuOpen}>
          <SidebarHeader open={isDrawOpen}>
            <Link
              component={RouterLink}
              to={RouteHelper.organisationDashboard}
              aria-label="theme-logo"
              title="Home"
              // No nudge: the logo row and the top bar are the same height, so
              // centring in each puts the logo on the crumbs axis exactly.
              sx={{ display: "inline-flex", flexShrink: 0 }}
            >
              {/* Both marks are mounted and cross-faded: swapping the
                  component made the logo pop while the column was still
                  folding. Flat brand colour, from `ui.logo.mark` — the
                  palette's readable step, NOT the nav's selected-row colour it
                  used to read, which is how it turned orange on Desert the
                  moment that row moved to the chart ramp. */}
              <SidebarLogo
                style={{
                  height: LOGO_HEIGHT,
                  width: isDrawOpen ? WORDMARK_WIDTH : MARK_WIDTH
                }}
              >
                <SidebarLogoLayer visible={isDrawOpen}>
                  <Logo
                    variant="catchment-wordmark"
                    width={WORDMARK_WIDTH}
                    height={LOGO_HEIGHT}
                    startColor={theme.vars.ui.logo.mark}
                    endColor={theme.vars.ui.logo.mark}
                  />
                </SidebarLogoLayer>
                <SidebarLogoLayer visible={!isDrawOpen}>
                  <Logo
                    variant="catchment"
                    width={MARK_WIDTH}
                    height={LOGO_HEIGHT}
                    startColor={theme.vars.ui.logo.mark}
                    endColor={theme.vars.ui.logo.mark}
                  />
                </SidebarLogoLayer>
              </SidebarLogo>
            </Link>
          </SidebarHeader>
          <SidebarRule open={isDrawOpen} />

          <ScopeSwitcher
            layoutType={layoutType}
            isDrawOpen={isDrawOpen}
            loading={isLoading}
            projects={projects}
            currentProject={currentProject}
            currentContractorPackageId={currentContractorPackageId}
            switchProject={switchProject}
            onOpenChange={setIsScopeMenuOpen}
          />

          <SidebarDimRegion open={isDrawOpen} dimmed={isScopeMenuOpen}>
            <PerfectScrollbar
              style={{ alignSelf: "stretch", flex: 1, minHeight: 0 }}
            >
              <Box
                sx={{
                  alignItems: "stretch",
                  display: "flex",
                  flexDirection: "column",
                  pb: 1
                }}
              >
                <MainSidebarMenu
                  loading={loading}
                  menuItem={menuItems}
                  isDrawOpen={isDrawOpen}
                  selectedItem={selectedItem}
                  setSelectedItem={setSelectedItem}
                />
              </Box>
            </PerfectScrollbar>
          </SidebarDimRegion>
        </SidebarDimRegion>

        <MainSidebarProfileSection
          isDrawOpen={isDrawOpen}
          loading={loading}
          onMenuOpenChange={setIsProfileMenuOpen}
        />
      </SidebarNav>

      {/* Centred on the nav's right edge, level with the logo row's rule. */}
      <SidebarToggleButton
        icon={isDrawOpen ? "collapse" : "expand"}
        label={isDrawOpen ? "Close sidebar" : "Open sidebar"}
        onClick={toggleSidebar}
        tooltip={isDrawOpen ? "Collapse navigation" : "Expand navigation"}
        tooltipPlacement="right"
        dataTestId={isDrawOpen ? "close-sidebar-btn" : "open-sidebar-btn"}
      />
    </SidebarContainer>
  );
};
